import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, Loader2, ArrowLeft, Search, Filter, Facebook, Instagram, User, CheckCheck, Clock, Info, Edit2, Save, X, RefreshCw, ChevronUp } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthProvider';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { pl } from 'date-fns/locale';
import { AudioPlayer } from './AudioPlayer';

interface MessageEvent {
  id: string;
  account_id: string;
  platform: 'facebook' | 'instagram' | 'instagram_direct';
  conversation_id: string;
  message_id: string;
  from_user: {
    id: string;
    name?: string;
    username?: string;
  };
  text: string | null;
  attachments: any | null;
  profile_picture_url?: string | null;
  raw: any;
  created_at: string;
  delivery_status?: 'pending' | 'sent' | 'delivered' | 'failed' | null;
  is_our_message: boolean;
}

interface Contact {
  id: string;
  platform_user_id: string;
  platform_username: string;
  custom_name: string | null;
  avatar_url: string | null;
}

interface Conversation {
  conversation_id: string;
  platform: 'facebook' | 'instagram' | 'instagram_direct';
  participant: {
    id: string;
    name?: string;
    username?: string;
  };
  last_message: string;
  last_message_time: string;
  unread_count: number;
  account_id: string;
  account_name: string;
  profile_picture_url?: string | null;
}

interface SocialAccount {
  id: string;
  platform: string;
  page_name: string;
  page_id: string;
  instagram_account_id?: string;
}

export function SocialMessages() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<MessageEvent[]>([]);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [contacts, setContacts] = useState<Map<string, Contact>>(new Map());
  const [editingContact, setEditingContact] = useState(false);
  const [customName, setCustomName] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [messagesLimit, setMessagesLimit] = useState(20);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      loadAccounts();
      loadContacts();
      loadConversations();
    }
  }, [user]);

  useEffect(() => {
    if (selectedConversation) {
      const contact = getContactForParticipant(selectedConversation.participant);
      setCustomName(contact?.custom_name || '');
    }
  }, [selectedConversation]);

  useEffect(() => {
    if (selectedConversation) {
      setMessagesLimit(20); // Reset limit
      loadMessages(selectedConversation.conversation_id, selectedConversation.account_id, 20);

      const channel = supabase
        .channel(`messages:${selectedConversation.account_id}:${selectedConversation.conversation_id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'message_events',
            filter: `conversation_id=eq.${selectedConversation.conversation_id},account_id=eq.${selectedConversation.account_id}`,
          },
          (payload) => {
            setMessages((prev) => [...prev, payload.new as MessageEvent]);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadAccounts = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('social_accounts')
      .select('id, platform, page_name, page_id, instagram_account_id')
      .eq('owner_user_id', user.id)
      .in('platform', ['facebook', 'instagram', 'instagram_direct']);

    if (error) {
      console.error('Error loading accounts:', error);
      return;
    }

    setAccounts(data || []);
  };

  const loadContacts = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('contacts')
      .select('*')
      .eq('user_id', user.id);

    if (error) {
      console.error('Error loading contacts:', error);
      return;
    }

    const contactsMap = new Map<string, Contact>();
    data?.forEach((contact) => {
      const key = `${contact.platform}_${contact.platform_user_id}`;
      contactsMap.set(key, contact);
    });

    setContacts(contactsMap);
  };

  const getContactForParticipant = (participant: { id: string; name?: string; username?: string }) => {
    if (!selectedConversation) return null;
    const key = `${selectedConversation.platform}_${participant.id}`;
    return contacts.get(key) || null;
  };

  const saveCustomName = async () => {
    if (!user || !selectedConversation) return;

    try {
      const participant = selectedConversation.participant;
      const contact = getContactForParticipant(participant);

      if (contact) {
        const { error } = await supabase
          .from('contacts')
          .update({ custom_name: customName || null })
          .eq('id', contact.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('contacts')
          .insert({
            user_id: user.id,
            platform: selectedConversation.platform,
            platform_user_id: participant.id,
            platform_username: participant.username || participant.name || participant.id,
            custom_name: customName || null,
          });

        if (error) throw error;
      }

      await loadContacts();
      setEditingContact(false);
      toast.success('Zapisano nazwę kontaktu');
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error('Nie udało się zapisać nazwy');
    }
  };

  const loadConversations = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { data: accountsData } = await supabase
        .from('social_accounts')
        .select('id, platform, page_name, page_id, instagram_account_id')
        .eq('owner_user_id', user.id)
        .in('platform', ['facebook', 'instagram', 'instagram_direct']);

      if (!accountsData || accountsData.length === 0) {
        setConversations([]);
        setLoading(false);
        return;
      }

      const accountIds = accountsData.map(a => a.id);

      const { data: messagesData, error } = await supabase
        .from('message_events')
        .select('*')
        .in('account_id', accountIds)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const conversationsMap = new Map<string, Conversation>();

      messagesData?.forEach((msg: MessageEvent) => {
        const key = `${msg.account_id}_${msg.conversation_id}`;
        const account = accountsData.find(a => a.id === msg.account_id);

        const isOurMessage = msg.is_our_message;

        if (!conversationsMap.has(key)) {
          conversationsMap.set(key, {
            conversation_id: msg.conversation_id,
            platform: msg.platform,
            participant: isOurMessage ? { id: 'unknown', name: 'Unknown' } : msg.from_user,
            last_message: msg.text || '(załącznik)',
            last_message_time: msg.created_at,
            unread_count: 0,
            account_id: msg.account_id,
            account_name: account?.page_name || 'Unknown',
            profile_picture_url: isOurMessage ? null : msg.profile_picture_url,
          });
        } else {
          const conv = conversationsMap.get(key)!;

          if (!isOurMessage && (conv.participant.name === 'Unknown' || conv.participant.id === 'unknown')) {
            conv.participant = msg.from_user;
            conv.profile_picture_url = msg.profile_picture_url;
          }
        }
      });

      setConversations(Array.from(conversationsMap.values()));
    } catch (error) {
      console.error('Error loading conversations:', error);
      toast.error('Nie udało się załadować konwersacji');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId: string, accountId: string, limit: number = 20, append: boolean = false) => {
    try {
      // First, get total count
      const { count } = await supabase
        .from('message_events')
        .select('*', { count: 'exact', head: true })
        .eq('conversation_id', conversationId)
        .eq('account_id', accountId);

      // Then get messages (latest first, then reverse for display)
      const { data, error } = await supabase
        .from('message_events')
        .select('*')
        .eq('conversation_id', conversationId)
        .eq('account_id', accountId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Reverse to show oldest first
      const reversedData = (data || []).reverse();

      if (append) {
        // When loading more, prepend older messages
        setMessages(prev => [...reversedData, ...prev]);
      } else {
        setMessages(reversedData);
      }

      // Check if there are more messages to load
      setHasMoreMessages((count || 0) > limit);

      // Auto-scroll to bottom only on initial load (not when loading more)
      if (!append) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
        }, 100);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Nie udało się załadować wiadomości');
    }
  };

  const loadMoreMessages = async () => {
    if (!selectedConversation || loadingMore || !hasMoreMessages) return;

    setLoadingMore(true);
    const currentScrollHeight = messagesContainerRef.current?.scrollHeight || 0;

    try {
      const newLimit = messagesLimit + 20;
      setMessagesLimit(newLimit);

      await loadMessages(selectedConversation.conversation_id, selectedConversation.account_id, newLimit, false);

      // Maintain scroll position
      setTimeout(() => {
        if (messagesContainerRef.current) {
          const newScrollHeight = messagesContainerRef.current.scrollHeight;
          messagesContainerRef.current.scrollTop = newScrollHeight - currentScrollHeight;
        }
      }, 100);
    } finally {
      setLoadingMore(false);
    }
  };

  const refreshInstagramDirectMessages = async () => {
    if (!user) return;

    setRefreshing(true);
    try {
      const { data: instagramAccounts } = await supabase
        .from('social_accounts')
        .select('id, page_name')
        .eq('owner_user_id', user.id)
        .eq('platform', 'instagram_direct')
        .eq('status', 'active');

      if (!instagramAccounts || instagramAccounts.length === 0) {
        toast.error('Brak kont Instagram Direct');
        return;
      }

      toast.loading('Pobieranie wiadomości Instagram Direct...');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/social-messages-poll`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          force_platform: 'instagram_direct',
          triggered_by: 'manual_refresh',
        }),
      });

      if (!response.ok) {
        throw new Error('Nie udało się pobrać wiadomości');
      }

      const result = await response.json();

      toast.dismiss();
      toast.success(`Pobrano wiadomości z ${result.accounts_processed || 0} kont`);

      await loadConversations();
      if (selectedConversation) {
        await loadMessages(selectedConversation.conversation_id, selectedConversation.account_id);
      }
    } catch (error) {
      console.error('Error refreshing messages:', error);
      toast.dismiss();
      toast.error('Nie udało się odświeżyć wiadomości');
    } finally {
      setRefreshing(false);
    }
  };

  const sendReply = async () => {
    if (!selectedConversation || !replyText.trim() || !user) return;

    setSending(true);
    try {
      const account = accounts.find(a => a.id === selectedConversation.account_id);
      if (!account) {
        throw new Error('Account not found');
      }

      const { data: messageEvent, error: messageError } = await supabase
        .from('message_events')
        .insert({
          account_id: selectedConversation.account_id,
          platform: selectedConversation.platform,
          conversation_id: selectedConversation.conversation_id,
          message_id: `pending_${Date.now()}`,
          from_user: {
            id: user.id,
            name: 'You',
          },
          text: replyText,
          raw: {
            is_outbound: true,
            status: 'pending',
          },
        })
        .select()
        .single();

      if (messageError) throw messageError;

      const contact = getContactForParticipant(selectedConversation.participant);

      const platformAccountId = (selectedConversation.platform === 'instagram' || selectedConversation.platform === 'instagram_direct')
        ? account.instagram_account_id
        : account.page_id;

      const { error: queueError } = await supabase.from('outbound_queue').insert({
        event_type: 'dm.outbound',
        ref_id: messageEvent.id,
        account_id: selectedConversation.account_id,
        target_webhook_url: import.meta.env.VITE_WEBHOOK_URL || 'https://webhook.site/your-webhook-id',
        payload: {
          platform: selectedConversation.platform,
          conversation_id: selectedConversation.conversation_id,
          recipient_id: selectedConversation.participant.id,
          message: replyText,
          account_id: platformAccountId,
          message_event_id: messageEvent.id,
          contact: {
            platform_user_id: selectedConversation.participant.id,
            platform_username: selectedConversation.participant.username || selectedConversation.participant.name,
            custom_name: contact?.custom_name || null,
          },
        },
        status: 'QUEUED',
        tries: 0,
        max_retries: 3,
      });

      if (queueError) throw queueError;

      toast.success('Wiadomość wysłana!');
      setReplyText('');

      await loadMessages(selectedConversation.conversation_id, selectedConversation.account_id);
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Nie udało się wysłać wiadomości');
    } finally {
      setSending(false);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'facebook':
        return <Facebook className="w-4 h-4 text-blue-500" />;
      case 'instagram':
      case 'instagram_direct':
        return <Instagram className="w-4 h-4 text-pink-500" />;
      default:
        return <MessageSquare className="w-4 h-4 text-gray-500" />;
    }
  };

  const getUserDisplayName = (user: { id: string; name?: string; username?: string }, platform?: string) => {
    const key = platform ? `${platform}_${user.id}` : `${selectedConversation?.platform}_${user.id}`;
    const contact = contacts.get(key);

    if (contact?.custom_name) return contact.custom_name;
    if (user.name) return user.name;
    if (user.username) return `@${user.username}`;
    return `User ${user.id.substring(0, 8)}`;
  };

  const getAvatarUrl = (user: { id: string; name?: string; username?: string }, platform?: string, profilePictureUrl?: string | null) => {
    if (profilePictureUrl) {
      return profilePictureUrl;
    }

    const displayName = getUserDisplayName(user, platform);
    const initials = displayName
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();

    const colors = [
      '7B4BFF', '3B82F6', 'EF4444', '10B981', 'F59E0B',
      'EC4899', '8B5CF6', '14B8A6', 'F97316', '06B6D4'
    ];

    const colorIndex = Math.abs(user.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % colors.length;
    const bgColor = colors[colorIndex];

    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${bgColor}&color=fff&size=128&bold=true&format=png`;
  };

  const filteredConversations = conversations.filter(conv => {
    const displayName = getUserDisplayName(conv.participant, conv.platform);
    const matchesSearch =
      displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.participant.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.last_message.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesPlatform = platformFilter === 'all' || conv.platform === platformFilter;

    return matchesSearch && matchesPlatform;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#7B4BFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-200px)] sm:h-[calc(100vh-250px)] bg-gray-800 rounded-lg overflow-hidden">
      <div className={`${selectedConversation ? 'hidden md:flex' : 'flex'} w-full md:w-96 flex-col border-r border-gray-700 overflow-hidden`}>
        <div className="p-4 border-b border-gray-700">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Szukaj konwersacji..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#7B4BFF]"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="flex-1 px-3 py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#7B4BFF]"
            >
              <option value="all">Wszystkie platformy</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="instagram_direct">Instagram Direct</option>
            </select>
            <button
              onClick={refreshInstagramDirectMessages}
              disabled={refreshing}
              title="Odśwież wiadomości Instagram Direct"
              className="p-1.5 bg-gray-900 border border-gray-700 rounded-lg hover:bg-gray-800 hover:border-[#7B4BFF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
              <RefreshCw className={`w-4 h-4 text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {platformFilter === 'instagram_direct' && (
            <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-3 mt-3">
              <div className="flex items-start space-x-2">
                <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-300">
                  <p className="font-medium mb-1">Instagram Direct Messages</p>
                  <p className="text-blue-200/80">
                    Wiadomości Instagram Direct są pobierane automatycznie co 5 minut.
                    Możesz kliknąć przycisk <RefreshCw className="w-3 h-3 inline" /> aby odświeżyć je teraz.
                  </p>
                </div>
              </div>
            </div>
          )}

          {platformFilter === 'instagram' && (
            <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-3 mt-3">
              <div className="flex items-start space-x-2">
                <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-300">
                  <p className="font-medium mb-1">Obsługa wiadomości Instagram</p>
                  <p className="text-blue-200/80">
                    Wiadomości prywatne na Instagramie są dostępne tylko dla kont podłączonych przez <span className="font-semibold">Instagram Direct</span>.
                    Konta Instagram podłączone przez Facebook nie obsługują wiadomości prywatnych.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-4">
              <MessageSquare className="w-12 h-12 mb-2" />
              <p className="text-center">Brak konwersacji</p>
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <button
                key={`${conv.account_id}_${conv.conversation_id}`}
                onClick={() => setSelectedConversation(conv)}
                className={`w-full p-4 text-left border-b border-gray-700 hover:bg-gray-700 transition-colors ${
                  selectedConversation?.conversation_id === conv.conversation_id ? 'bg-gray-700' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="relative">
                    <img
                      src={getAvatarUrl(conv.participant, conv.platform, conv.profile_picture_url)}
                      alt={getUserDisplayName(conv.participant, conv.platform)}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="absolute -bottom-1 -right-1 bg-gray-800 rounded-full p-0.5">
                      {getPlatformIcon(conv.platform)}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <p className="font-medium text-white truncate flex-1">
                        {getUserDisplayName(conv.participant, conv.platform)}
                      </p>
                      <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                        {formatDistanceToNow(new Date(conv.last_message_time), {
                          addSuffix: true,
                          locale: pl,
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 truncate mb-1">
                      {conv.last_message}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {conv.account_name}
                    </p>
                  </div>

                  {conv.unread_count > 0 && (
                    <div className="flex-shrink-0 w-6 h-6 bg-[#7B4BFF] rounded-full flex items-center justify-center">
                      <span className="text-xs text-white font-bold">
                        {conv.unread_count}
                      </span>
                    </div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className={`${selectedConversation ? 'flex' : 'hidden md:flex'} flex-1 flex-col`}>
        {selectedConversation ? (
          <>
            <div className="p-4 border-b border-gray-700 flex items-center space-x-3">
              <button
                onClick={() => setSelectedConversation(null)}
                className="md:hidden p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>

              <div className="relative">
                <img
                  src={getAvatarUrl(selectedConversation.participant, selectedConversation.platform, selectedConversation.profile_picture_url)}
                  alt={getUserDisplayName(selectedConversation.participant)}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="absolute -bottom-1 -right-1 bg-gray-800 rounded-full p-0.5">
                  {getPlatformIcon(selectedConversation.platform)}
                </div>
              </div>

              <div className="flex-1 min-w-0 overflow-hidden">
                {editingContact ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                      placeholder="Nazwa kontaktu..."
                      className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-[#7B4BFF]"
                      autoFocus
                    />
                    <button
                      onClick={saveCustomName}
                      className="p-1.5 bg-green-600 hover:bg-green-700 rounded transition-colors flex-shrink-0"
                    >
                      <Save className="w-4 h-4 text-white" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingContact(false);
                        const contact = getContactForParticipant(selectedConversation.participant);
                        setCustomName(contact?.custom_name || '');
                      }}
                      className="p-1.5 bg-red-600 hover:bg-red-700 rounded transition-colors flex-shrink-0"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-white truncate">
                        {getUserDisplayName(selectedConversation.participant)}
                      </h3>
                      <p className="text-xs text-gray-400 truncate">
                        ID: {selectedConversation.participant.id.substring(0, 12)}...
                      </p>
                    </div>
                    <button
                      onClick={() => setEditingContact(true)}
                      className="p-1.5 hover:bg-gray-700 rounded transition-colors"
                      title="Edytuj nazwę kontaktu"
                    >
                      <Edit2 className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
              {hasMoreMessages && (
                <div className="flex justify-center mb-4">
                  <button
                    onClick={loadMoreMessages}
                    disabled={loadingMore}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white flex items-center space-x-2 transition-colors"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Ładowanie...</span>
                      </>
                    ) : (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        <span>Wczytaj starsze wiadomości</span>
                      </>
                    )}
                  </button>
                </div>
              )}
              {messages
                .filter((msg, index, arr) => {
                  const isManualMessage = msg.from_user.name === 'You' || msg.from_user.id === user?.id;
                  if (isManualMessage) return true;

                  const isDuplicate = arr.some((other, otherIndex) =>
                    otherIndex < index &&
                    other.text === msg.text &&
                    Math.abs(new Date(other.created_at).getTime() - new Date(msg.created_at).getTime()) < 300000 &&
                    other.is_our_message
                  );

                  return !isDuplicate;
                })
                .map((msg) => {
                const isManualMessage = msg.from_user.name === 'You' || msg.from_user.id === user?.id;
                const isWebhookMessage = msg.is_our_message && !isManualMessage;
                const isOurMessage = msg.is_our_message;

                return (
                  <div
                    key={msg.id}
                    className={`flex ${isOurMessage ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-2xl p-3 shadow-lg ${
                        isOurMessage
                          ? isManualMessage
                            ? 'bg-gradient-to-br from-[#7B4BFF] to-[#9D6CFF] text-white rounded-br-sm'
                            : 'bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-br-sm'
                          : 'bg-gray-700 text-white rounded-bl-sm'
                      }`}
                    >
                      {!isOurMessage ? (
                        <p className="text-xs font-medium mb-1 opacity-75">
                          {getUserDisplayName(msg.from_user)}
                        </p>
                      ) : (
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-medium opacity-75">
                            {selectedConversation.account_name}
                          </p>
                          {isWebhookMessage && (
                            <span className="text-xs opacity-60 ml-2 flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              auto
                            </span>
                          )}
                        </div>
                      )}

                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="space-y-2 mb-2">
                          {msg.attachments.map((att: any, idx: number) => {
                            const isImage = att.type?.startsWith('image/');
                            const isAudio = att.type?.startsWith('audio/');
                            const isVideo = att.type?.startsWith('video/');

                            if (isImage) {
                              return (
                                <div key={idx} className="rounded-lg overflow-hidden">
                                  <img
                                    src={att.url || att.preview_url}
                                    alt={att.name || 'Obrazek'}
                                    className="max-w-full h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() => window.open(att.url || att.preview_url, '_blank')}
                                  />
                                </div>
                              );
                            }

                            if (isAudio) {
                              return (
                                <AudioPlayer
                                  key={idx}
                                  attachment={att}
                                  messageId={msg.message_id}
                                />
                              );
                            }

                            if (isVideo) {
                              return (
                                <div key={idx} className="rounded-lg overflow-hidden">
                                  {att.url ? (
                                    <>
                                      <video controls className="max-w-full h-auto rounded-lg">
                                        <source src={att.url} type={att.type} />
                                        Twoja przeglądarka nie obsługuje video.
                                      </video>
                                      {att.name && (
                                        <p className="text-xs opacity-70 mt-1">{att.name}</p>
                                      )}
                                    </>
                                  ) : (
                                    <div className="bg-black/20 rounded-lg p-3">
                                      <div className="flex items-center space-x-2 text-gray-400 text-sm">
                                        <Info className="w-4 h-4 flex-shrink-0" />
                                        <div>
                                          <p className="font-medium">Wiadomość video</p>
                                          {att.name && (
                                            <p className="text-xs opacity-70">{att.name}</p>
                                          )}
                                          <p className="text-xs opacity-70 mt-1">
                                            URL wygasł lub niedostępny
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            return (
                              <div key={idx} className="bg-black/20 rounded-lg p-2 text-xs">
                                <a
                                  href={att.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline flex items-center space-x-2"
                                >
                                  <span>📄</span>
                                  <span>{att.name || 'Załącznik'}</span>
                                  {att.size && (
                                    <span className="opacity-60">({Math.round(att.size / 1024)} KB)</span>
                                  )}
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Voice message fallback - no text, no attachments */}
                      {!msg.text && (!msg.attachments || msg.attachments.length === 0) && (
                        <div className="flex items-start gap-3 p-3 bg-black/10 rounded-lg border border-white/10">
                          <div className="flex-shrink-0 mt-0.5">
                            <svg className="w-5 h-5 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white/90">
                              Wiadomość głosowa
                            </p>
                            <p className="text-xs text-white/60 mt-1">
                              Instagram API nie udostępnia audio przez polling.
                              {' '}
                              <a
                                href={`https://instagram.com/direct/t/${msg.conversation_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300 underline"
                              >
                                Otwórz w Instagram →
                              </a>
                            </p>
                          </div>
                        </div>
                      )}

                      {msg.text && (
                        <p className="text-sm whitespace-pre-wrap break-words">
                          {msg.text}
                        </p>
                      )}

                      <div className="flex items-center justify-end space-x-1 mt-1">
                        <span className="text-xs opacity-75">
                          {new Date(msg.created_at).toLocaleTimeString('pl-PL', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        {isManualMessage ? (
                          msg.delivery_status === 'delivered' ? (
                            <CheckCheck className="w-3 h-3 text-green-400" title="Dostarczone" />
                          ) : msg.delivery_status === 'failed' ? (
                            <X className="w-3 h-3 text-red-400" title="Błąd wysyłania" />
                          ) : msg.delivery_status === 'sent' ? (
                            <Clock className="w-3 h-3 text-blue-400 animate-pulse" title="Wysyłanie..." />
                          ) : (
                            <Clock className="w-3 h-3 text-orange-400 animate-pulse" title="Oczekuje..." />
                          )
                        ) : isWebhookMessage ? (
                          <CheckCheck className="w-3 h-3" />
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className="p-4 border-t border-gray-700">
              <div className="flex items-end space-x-2">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendReply();
                    }
                  }}
                  placeholder="Napisz wiadomość..."
                  rows={3}
                  className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#7B4BFF] resize-none"
                />
                <button
                  onClick={sendReply}
                  disabled={!replyText.trim() || sending}
                  className="px-4 py-2 bg-[#7B4BFF] text-white rounded-lg hover:bg-[#6A3FEF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors h-[84px] flex items-center justify-center"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Enter - wyślij, Shift+Enter - nowa linia
              </p>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg mb-2">Wybierz konwersację</p>
              <p className="text-sm">
                Kliknij na konwersację po lewej stronie, aby zobaczyć wiadomości
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
